// AiFon — root app.
// Router:
//   `/`            Home (C01, r1)
//   `/sinyal/:fund` SinyalDetay (C04, r1)
//   `/anomaliler`  Anomaliler (r2 — CR2-09)
//   `/sinyaller`   Sinyaller (r2 — CR2-05)
//   `/anne`        AnneDefteri (r2 — CR2-01 + CR2-11)
//   `/ayarlar`     Settings modal (Home arkasında)
//
// App-level state:
//   - evaluatedFunds      — r1 C04 "değerlendirildi" rozeti (mevcut)
//   - openAnomalies     — backend /anomalies/open snapshot'ı (App owns; header
//                         rozet, /anomaliler, /sinyaller, /anne için tek source)
//   - anomaliesLoading  — ilk yükleme spinner'ı için
//   - reloadAnomalies   — Anomaliler sayfasından resolve sonrası refresh
//   - handleResolveAnomaly — POST /anomalies/:id/resolve + optimistic remove

import { useCallback, useEffect, useState } from "react";
import { BrowserRouter, Routes, Route, useNavigate } from "react-router-dom";
import {
  loadConfig,
  getAnomaliesOpen,
  resolveAnomaly as apiResolveAnomaly,
  ApiError,
  type ApiConfig,
} from "./lib/api";
import type { Anomaly } from "./types/api";
import Settings from "./components/Settings";
import Home from "./pages/Home";
import SinyalDetay from "./pages/SinyalDetay";
import Anomaliler from "./pages/Anomaliler";
import Sinyaller from "./pages/Sinyaller";
import AnneDefteri from "./pages/AnneDefteri";
import Es from "./pages/Es";

function AyarlarRoute({
  cfg,
  setCfg,
}: {
  cfg: ApiConfig;
  setCfg: (c: ApiConfig) => void;
}) {
  const navigate = useNavigate();
  return (
    <Settings
      initial={cfg}
      onClose={() => navigate("/")}
      onSaved={(next) => {
        setCfg(next);
        navigate("/");
      }}
    />
  );
}

export default function App() {
  const [cfg, setCfg] = useState<ApiConfig>(() => loadConfig());

  // r1: değerlendirildi rozeti — C04 her açıldığında ilgili fund işaretlenir.
  const [evaluatedFunds, setEvaluatedFunds] = useState<Set<string>>(() => new Set());
  const markEvaluated = useCallback((fund: string) => {
    setEvaluatedFunds((prev) => {
      if (prev.has(fund)) return prev;
      const next = new Set(prev);
      next.add(fund);
      return next;
    });
  }, []);

  // r2: açık anomaliler — App-level snapshot. Header rozet sayısı +
  // /sinyaller InlineAnomalyChip + /anne audit chip aynı array'den okuyor.
  const [openAnomalies, setOpenAnomalies] = useState<Anomaly[]>([]);
  const [anomaliesLoading, setAnomaliesLoading] = useState(true);
  const [anomaliesError, setAnomaliesError] = useState<string | null>(null);

  const reloadAnomalies = useCallback(async () => {
    if (!cfg.base) {
      setAnomaliesLoading(false);
      return;
    }
    setAnomaliesLoading(true);
    setAnomaliesError(null);
    try {
      const out = await getAnomaliesOpen(cfg);
      setOpenAnomalies(out.items);
    } catch (e) {
      const msg =
        e instanceof ApiError
          ? `${e.message} (HTTP ${e.status})`
          : e instanceof Error
            ? e.message
            : String(e);
      setAnomaliesError(msg);
    } finally {
      setAnomaliesLoading(false);
    }
  }, [cfg]);

  useEffect(() => {
    void reloadAnomalies();
  }, [reloadAnomalies]);

  // Anomali resolve — POST + optimistic remove. Hata olursa state restore.
  const handleResolveAnomaly = useCallback(
    async (id: string): Promise<void> => {
      const prev = openAnomalies;
      setOpenAnomalies((cur) => cur.filter((a) => a.id !== id));
      try {
        await apiResolveAnomaly(cfg, id);
      } catch (e) {
        setOpenAnomalies(prev);
        throw e;
      }
    },
    [cfg, openAnomalies],
  );

  const openAnomaliesCount = openAnomalies.length;

  return (
    <BrowserRouter>
      <Routes>
        <Route
          path="/"
          element={
            <Home
              cfg={cfg}
              evaluatedFunds={evaluatedFunds}
              openAnomaliesCount={openAnomaliesCount}
            />
          }
        />
        <Route
          path="/sinyal/:fund"
          element={<SinyalDetay markEvaluated={markEvaluated} />}
        />
        <Route
          path="/anomaliler"
          element={
            <Anomaliler
              cfg={cfg}
              openAnomalies={openAnomalies}
              openAnomaliesCount={openAnomaliesCount}
              loading={anomaliesLoading}
              error={anomaliesError}
              onResolve={handleResolveAnomaly}
              onReload={reloadAnomalies}
            />
          }
        />
        <Route
          path="/sinyaller"
          element={
            <Sinyaller
              cfg={cfg}
              openAnomalies={openAnomalies}
              openAnomaliesCount={openAnomaliesCount}
            />
          }
        />
        <Route
          path="/anne"
          element={
            <AnneDefteri
              cfg={cfg}
              openAnomalies={openAnomalies}
              openAnomaliesCount={openAnomaliesCount}
            />
          }
        />
        <Route path="/es" element={<Es cfg={cfg} />} />
        <Route
          path="/ayarlar"
          element={
            <>
              <Home
                cfg={cfg}
                evaluatedFunds={evaluatedFunds}
                openAnomaliesCount={openAnomaliesCount}
              />
              <AyarlarRoute cfg={cfg} setCfg={setCfg} />
            </>
          }
        />
      </Routes>
    </BrowserRouter>
  );
}
